import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getAllGameStories,
  updateGameStory,
  deleteGameStory,
  generateStoryQRCode
} from '../../utils/api';
import LoadingState from '../../components/LoadingState';
import { QRCodeSVG } from 'qrcode.react';

// Icons
import {
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  QrCodeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const StoryManager = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeGenerating, setQrCodeGenerating] = useState(false);
  
  // Show success message for 3 seconds then hide it
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Fetch stories on mount
  useEffect(() => {
    fetchStories();
  }, []);
  
  // Fetch all stories
  const fetchStories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllGameStories();
      
      if (response && response.success) {
        // Set stories as returned from backend
        setStories(response.data || []);
      } else {
        throw new Error((response && response.message) || 'ストーリーの取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
      setError('ストーリーの読み込みに失敗しました。後でもう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening the delete confirmation modal
  const handleOpenDeleteConfirm = (story) => {
    setSelectedStory(story);
    setShowDeleteConfirm(true);
  };
  
  // Handle deleting a story
  const handleDeleteStory = async () => {
    if (!selectedStory) return;
    
    try {
      const response = await deleteGameStory(selectedStory.storyId);
      
      if (response && response.success) {
        setSuccessMessage(`ストーリーが削除されました: ${selectedStory.title}`);
        setStories(stories.filter(story => story.storyId !== selectedStory.storyId));
        setShowDeleteConfirm(false);
        setSelectedStory(null);
      } else {
        throw new Error((response && response.message) || 'ストーリーの削除に失敗しました');
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      setError(`ストーリーの削除エラー: ${error.message}`);
    }
  };
  
  // Handle generating QR code for a story
  const handleGenerateQRCode = async (story) => {
    setSelectedStory(story);
    setQrCodeGenerating(true);
    setShowQRCodeModal(true);
    
    try {
      const baseUrl = window.location.origin;
      const storyUrl = `${baseUrl}/story/${story.storyId}`;
      setQrCodeUrl(storyUrl);
      
      // First attempt to fix any missing questionText fields
      try {
        // Auto-fix missing questionText fields directly
        if (story.questionsAnswers && story.questionsAnswers.length > 0) {
          const updatedQuestions = story.questionsAnswers.map((question, index) => {
            if (!question.questionText) {
              return { ...question, questionText: `Question ${index + 1}` };
            }
            return question;
          });
          
          // Update the story with fixed questions
          if (JSON.stringify(updatedQuestions) !== JSON.stringify(story.questionsAnswers)) {
            console.log('Fixing missing questionText fields...');
            await updateGameStory(story.storyId, { ...story, questionsAnswers: updatedQuestions });
            console.log('Story updated with fixed questions');
          }
        }
      } catch (fixError) {
        console.error('Error fixing missing question fields:', fixError);
      }
      
      // Generate QR code on server
      await generateStoryQRCode(story.storyId, { url: storyUrl });
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError(`QRコード生成エラー: ${error.message || '不明なエラー'}`);
      
      // If still showing modal when error occurs
      if (showQRCodeModal) {
        // Still show a local QR code even if server-side generation failed
        const baseUrl = window.location.origin;
        const storyUrl = `${baseUrl}/story/${story.storyId}`;
        setQrCodeUrl(storyUrl);
      }
    } finally {
      setQrCodeGenerating(false);
    }
  };
  
  // Render the stories list
  const renderStoriesList = () => {
    if (stories.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">ストーリーが見つかりません。ゲームエディターで新しいストーリーを作成してください。</p>
          <Link 
            to="/admin/game-editor" 
            className="mt-4 inline-block bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            新しいストーリーを作成
          </Link>
        </div>
      );
    }
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                タイトル
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                説明
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                質問数
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                アクション
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stories.map((story) => (
              <tr key={story.storyId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{story.title}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500 truncate max-w-[200px]">
                    {story.description || '説明なし'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {story.questionsAnswers ? story.questionsAnswers.length : 0}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/admin/game-editor/${story.storyId}`)}
                      className="p-1 rounded-full hover:bg-blue-100 text-blue-600"
                      title="編集"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleGenerateQRCode(story)}
                      className="p-1 rounded-full hover:bg-purple-100 text-purple-600"
                      title="QRコード生成"
                    >
                      <QrCodeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteConfirm(story)}
                      className="p-1 rounded-full hover:bg-red-100 text-red-600"
                      title="削除"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Delete confirmation modal
  const renderDeleteConfirmModal = () => {
    if (!showDeleteConfirm) return null;
    
    return (
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    ストーリーを削除
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      ストーリー「{selectedStory?.title}」を削除してもよろしいですか？この操作は元に戻せません。
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleDeleteStory}
              >
                削除
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // QR Code modal
  const renderQRCodeModal = () => {
    if (!showQRCodeModal) return null;
    
    return (
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {selectedStory?.title}のQRコード
                </h3>
                <button
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => setShowQRCodeModal(false)}
                >
                  <span className="sr-only">閉じる</span>
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mt-4 flex flex-col items-center justify-center">
                {qrCodeGenerating ? (
                  <div className="text-center py-8">
                    <ArrowPathIcon className="h-8 w-8 mx-auto animate-spin text-indigo-600" />
                    <p className="mt-2 text-sm text-gray-500">QRコードを生成中...</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white p-4 rounded-lg shadow">
                      <QRCodeSVG 
                        id="story-qrcode"
                        value={qrCodeUrl} 
                        size={200}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    <p className="mt-4 text-sm text-gray-500 break-all text-center">
                      {qrCodeUrl}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => {
                  const svg = document.getElementById('story-qrcode');
                  if (!svg) return;
                  
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  const img = new Image();
                  
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    const pngFile = canvas.toDataURL('image/png');
                    
                    const downloadLink = document.createElement('a');
                    downloadLink.download = `story-${selectedStory?.storyId}-qr.png`;
                    downloadLink.href = pngFile;
                    downloadLink.click();
                  };
                  
                  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                }}
              >
                QRコードをダウンロード
              </button>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => {
                  navigator.clipboard.writeText(qrCodeUrl);
                  setSuccessMessage('URLがクリップボードにコピーされました');
                }}
              >
                URLをコピー
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setShowQRCodeModal(false)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Main render
  if (loading) {
    return <LoadingState message="ストーリーを読み込み中..." />;
  }
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ストーリー管理</h1>
        <p className="text-gray-600 mt-1">ゲームストーリーの管理と有効化</p>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-4 flex justify-between items-center">
        <div>
          <button
            onClick={fetchStories}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            更新
          </button>
        </div>
        <Link
          to="/admin/game-editor"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          新しいストーリーを作成
          </Link>
        </div>
        
        {/* Success message */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
          </div>
        )}
        
        {/* Content */}
        {renderStoriesList()}
        
        {/* Modals */}
        {renderDeleteConfirmModal()}
        {renderQRCodeModal()}
      </div>
    </div>
  );
};

export default StoryManager;
